import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  Alert,
  useMantineTheme,
  FocusTrap,
  PinInput,
  Center,
  ThemeIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck } from "@tabler/icons-react";
import EnglishLoadingWithCompletion from "./english-loading-with-completion";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";
import { normalizeDob } from "../utils/validateDob";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../utils/edge";

const getFieldDescriptor = (field) => {
  const fieldName =
    typeof field.name === "string" ? field.name.toLowerCase() : "";

  switch (fieldName) {
    case "fn":
    case "first_name":
    case "firstname":
      return "First name";
    case "ln":
    case "last_name":
    case "lastname":
      return "Last name";
    case "phone":
    case "phone_number":
    case "phonenumber":
      return "Phone";
    case "dob":
    case "dateofbirth":
    case "date_of_birth":
      return "Date of birth";
    case "ssn":
    case "last4ssn":
    case "lastfourssn":
    case "ssn4":
      return "SSN";
    case "address":
      return "Address";
    default:
      if (typeof field.stepLabel === "string") {
        return field.stepLabel;
      }
      if (typeof field.label === "string") {
        return field.label;
      }
      return fieldName || "Field";
  }
};

const formatStepTitle = (labels) => {
  const uniqueLabels = Array.from(new Set(labels.filter(Boolean)));

  if (uniqueLabels.length === 0) {
    return "Additional details";
  }

  if (uniqueLabels.length === 1) {
    return `${uniqueLabels[0]} details`;
  }

  if (uniqueLabels.length === 2) {
    return `${uniqueLabels[0]} and ${uniqueLabels[1]} details`;
  }

  const lastLabel = uniqueLabels[uniqueLabels.length - 1];
  const initialLabels = uniqueLabels.slice(0, -1);
  return `${initialLabels.join(", ")}, and ${lastLabel} details`;
};

const PhoneMatchForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();

  // Edge mode detection
  const isEdgeMode = data?.edge === true;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [phoneMatchStep, setPhoneMatchStep] = useState(0); // Index of the current data entry step
  const [isOtpInput, setIsOtpInput] = useState(false);
  const [template, setTemplate] = useState({});
  const [hasHandledFailure, setHasHandledFailure] = useState(false);
  const inputSize = useAdjustInputSize();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [processingVerification, setProcessingVerification] = useState(false);
  const [loaderComplete, setLoaderComplete] = useState(false);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Get disabled fields from journey route configuration
  const getDisabledFields = () => {
    const journey = settings?.journey;
    if (!journey?.route) return [];

    const currentRoute = journey.route.find(route => route.path === "phonematch");
    return currentRoute?.component?.disabled || [];
  };

  const disabledFields = getDisabledFields();

  // Get skipable text from journey route configuration
  const getSkipableText = () => {
    const journey = settings?.journey;
    if (!journey?.route) return null;

    const currentRoute = journey.route.find(route => route.path === "phonematch");
    return currentRoute?.skipable && currentRoute?.skipableText ? currentRoute.skipableText : null;
  };

  const skipableText = getSkipableText();

  // Skip handler - navigate to next route
  const handleSkip = () => {
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
    dispatch({ type: "SET_STATUS", payload: "PENDING" });
    dispatch({ type: "TRIGGER_NAVIGATION", payload: true });
  };

  const { nameFields, dobFields, addressFields } = useMemo(
    () =>
      config.fields.reduce(
        (acc, field) => {
          const groupName =
            typeof field.group === "string" ? field.group.toLowerCase() : null;
          const fieldName =
            typeof field.name === "string" ? field.name.toLowerCase() : "";

          let normalizedGroup = groupName;
          if (normalizedGroup === "phone_dob") {
            if (
              fieldName === "phone" ||
              fieldName === "phone_number" ||
              fieldName === "phonenumber"
            ) {
              normalizedGroup = "name";
            } else {
              normalizedGroup = "dob";
            }
          }

          if (!normalizedGroup) {
            if (field.type === "address" || fieldName === "address") {
              normalizedGroup = "address";
            } else if (
              fieldName === "fn" ||
              fieldName === "first_name" ||
              fieldName === "firstname" ||
              fieldName === "ln" ||
              fieldName === "last_name" ||
              fieldName === "lastname"
            ) {
              normalizedGroup = "name";
            } else if (
              fieldName === "phone" ||
              fieldName === "phone_number" ||
              fieldName === "phonenumber"
            ) {
              normalizedGroup = "name";
            } else if (
              fieldName === "dob" ||
              fieldName === "dateofbirth" ||
              fieldName === "date_of_birth"
            ) {
              normalizedGroup = "dob";
            } else if (
              fieldName === "ssn" ||
              fieldName === "last4ssn" ||
              fieldName === "lastfourssn" ||
              fieldName === "ssn4"
            ) {
              normalizedGroup = "dob";
            }
          }

          switch (normalizedGroup) {
            case "name":
              acc.nameFields.push(field);
              break;
            case "dob":
              acc.dobFields.push(field);
              break;
            case "address":
              acc.addressFields.push(field);
              break;
            default:
              acc.dobFields.push(field);
              break;
          }

          return acc;
        },
        { nameFields: [], dobFields: [], addressFields: [] },
      ),
    [config.fields],
  );

  const phoneMatchSteps = useMemo(() => {
    const steps = [];
    if (nameFields.length > 0) {
      steps.push({ key: "name", fields: nameFields });
    }
    if (dobFields.length > 0) {
      steps.push({ key: "dob", fields: dobFields });
    }
    if (addressFields.length > 0) {
      steps.push({ key: "address", fields: addressFields });
    }
    return steps;
  }, [nameFields, dobFields, addressFields]);

  const hasSteps = phoneMatchSteps.length > 0;
  const lastEditableStepIndex = hasSteps ? phoneMatchSteps.length - 1 : 0;
  const currentStep = !isOtpInput
    ? phoneMatchSteps[Math.min(phoneMatchStep, lastEditableStepIndex)] || null
    : null;

  useEffect(() => {
    if (isOtpInput) return;
    if (!hasSteps) {
      if (phoneMatchStep !== 0) {
        setPhoneMatchStep(0);
      }
      return;
    }

    if (phoneMatchStep > lastEditableStepIndex) {
      setPhoneMatchStep(lastEditableStepIndex);
    } else if (phoneMatchStep < 0) {
      setPhoneMatchStep(0);
    }
  }, [hasSteps, lastEditableStepIndex, phoneMatchStep, isOtpInput]);

  const getPhoneMatchStepTitle = () => {
    if (isOtpInput) return "Enter verification code";
    if (!currentStep) return "Verification details";

    if (currentStep.key === "name") {
      if (currentStep.fields.length === 1) {
        return formatStepTitle(currentStep.fields.map(getFieldDescriptor));
      }
      return "Name details";
    }

    return formatStepTitle(currentStep.fields.map(getFieldDescriptor));
  };

  const isPhoneMatchStepValid = () => {
    if (isOtpInput) {
      return form.values.pin && form.values.pin.length === 6;
    }

    const fieldsToValidate = currentStep?.fields || [];
    return fieldsToValidate.every((field) => {
      if (field.type === "address") {
        const addressValue = {
          addr: form.values.addr || "",
          city: form.values.city || "",
          state: form.values.state || "",
          zip: form.values.zip || "",
        };
        const validationResult = field.validation
          ? field.validation(addressValue)
          : null;
        return validationResult === null;
      }

      const value = form.values[field.name] || "";
      const validationResult = field.validation ? field.validation(value) : null;
      return validationResult === null;
    });
  };

  // Status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      setProcessingVerification(false);
      return true;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setProcessingVerification(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        // Trigger navigation even on failure when attempts exhausted
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "TRIGGER_NAVIGATION", payload: true });
        setLoading(true);
        return true;
      }

      setLoading(true);
      return true;
    }

    // If we're not waiting for confirmation but attempts are exhausted and status is not PASS, trigger navigation
    if (statusResult.status !== "PASS" && statusResult.status !== "NEW" && attemptsLeft === 0 && !waitingForConfirmationChangeRef.current) {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "TRIGGER_NAVIGATION", payload: true });
      setLoading(true);
      return true;
    }

    return true;
  };

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
  }, [showIntro]);

  // Reset to first step on reattempt
  useEffect(() => {
    if (attemptsLeft < reAttempts && hasReceivedFailedResponse) {
      setPhoneMatchStep(0);
      setIsOtpInput(false);
      setTemplate({});
      setEdgeStatusPolling(false);
      form.clearErrors();
      setHasReceivedFailedResponse(false);
    }
  }, [attemptsLeft, reAttempts, hasReceivedFailedResponse]);

  // Status check hooks - use edge status check when in edge mode
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, { pin: "" }),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};

      if (isOtpInput) {
        if (values.pin.length < 6) {
          errors.pin = text?.phonematch?.code?.error || "Code is required";
        }
        return errors;
      }

      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Edge mode: poll retrieve endpoint for phonematch result (OTP template or direct result)
  const pollForResult = async (requestId) => {
    const maxAttempts = 100; // ~5 minutes
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            request_id: requestId,
            type: "phonematch",
          }),
        });

        if (response.ok) {
          const result = await response.json();

          // Check if OTP is needed (PENDING with template)
          if (result.action === "PENDING" && result.template) {
            setTemplate(result.template);
            setIsOtpInput(true);
            setLoading(false);
            setProcessingVerification(false);
            setIsVerifying(false);
            return; // Stop polling, show OTP input
          }

          // Check for final status without OTP
          if (result.status === "PASS" || result.status === "FAIL" ||
              result.action === "PASS" || result.action === "FAIL") {
            setIsVerifying(false);
            setProcessingVerification(true);
            setLoaderComplete(false);
            setEdgeStatusPolling(true);
            return; // Stop polling, edge status check handles the rest
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("PhoneMatch polling timeout: No result received");
          setIsVerifying(false);
          setLoading(false);
          setProcessingVerification(false);
        }
      } catch (error) {
        console.error("PhoneMatch polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsVerifying(false);
          setLoading(false);
          setProcessingVerification(false);
        }
      }
    };

    poll();
  };

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    // Update data with form values
    Object.keys(form.values).forEach((key) => {
      if (key !== "pin") {
        data[key] = form.values[key];
      }
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;
    data.isOtpInput = isOtpInput;

    if (isOtpInput) {
      template.codematch.key = form.values.pin;
      data.phonematch_continue = template;
    }

    setLoading(true);

    // Prepare request data and URL based on edge mode
    let requestData = { ...data };
    let apiUrl;

    if (isEdgeMode) {
      apiUrl = getEdgeQueueUrl();
      requestData.queue = "phonematch";
    } else {
      apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((responseData) => {
          // Edge mode handling
          if (isEdgeMode) {
            const reqId = responseData.request_id || settings.request_id;
            if (responseData.request_id) {
              dispatch({ type: "SET_SETTINGS", payload: { ...settings, request_id: responseData.request_id } });
            }

            if (isOtpInput) {
              // OTP was submitted, start edge status polling for final result
              setProcessingVerification(true);
              setLoaderComplete(false);
              setIsVerifying(false);
              setEdgeStatusPolling(true);
            } else {
              // Initial submit, poll for result (may return OTP template or direct result)
              if (reqId) {
                pollForResult(reqId);
              } else {
                console.error("No request_id found in edge response");
                setIsVerifying(false);
                setLoading(false);
              }
            }
            return;
          }

          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          // Handle PhoneMatch specific responses (non-edge mode)
          if (responseData.result.action === "PENDING" && !isOtpInput) {
            setLoading(false);
            setTemplate(responseData.continuations.outofband.template);
            setIsOtpInput(true);
            setIsVerifying(false);
            return;
          } else if (responseData.result.action === "PASS") {
            try {
              dispatch({ type: "SET_STATUS", payload: "PASS" });
              dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            } catch (err) {
              // Silent fail
            }
            setLoading(true);
            setProcessingVerification(false);
            return;
          } else {
            // Handle failure
            if (!hasHandledFailure) {
              setHasHandledFailure(true);
              try {
                dispatch({ type: "SET_STATUS", payload: "FAIL" });
                dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
                dispatch({ type: "TRIGGER_NAVIGATION", payload: true });
              } catch (err) {
                // Silent fail
              }
            }
            setLoading(true);
            return;
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setProcessingVerification(true);
            setLoaderComplete(false);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);
          setHasShownFormForCurrentAttempt(false);
          waitingForConfirmationChangeRef.current = true;
          setResult(true);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  const handleDobChange = (dob) => {
    const formattedDob = normalizeDob(`${dob.year || ""}${dob.month || ""}${dob.day || ""}`);
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  const getNewPin = () => {
    setIsOtpInput(false);
    const lastStepIndex = phoneMatchSteps.length > 0 ? phoneMatchSteps.length - 1 : 0;
    setPhoneMatchStep(lastStepIndex);
    setTemplate({});
    setResult(false);
    setEdgeStatusPolling(false);
    form.setFieldValue("pin", "");
  };

  const getButtonType = () => {
    if (!hasSteps) {
      return "submit";
    }
    return phoneMatchStep >= lastEditableStepIndex ? "submit" : "button";
  };

  const getButtonText = () => {
    if (!hasSteps || phoneMatchStep >= lastEditableStepIndex) {
      return buttonText;
    }
    return "Continue";
  };

  const animatedSectionStyle = useMemo(
    () => ({
      animation: `slideUpBounce 0.5s ${bounceTransition}`,
    }),
    [bounceTransition],
  );

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);
    setLoading(false);
    setEdgeStatusPolling(false);
    form.clearErrors();
  }, [componentKey]);

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  if (loading && !isVerifying) {
    if (processingVerification && isEnglish) {
      const verificationMessages = [
        "Verifying phone number...",
        "Checking personal information...",
        "Validating details...",
        "Processing verification request...",
        "Finalizing phone verification...",
      ];

      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1800,
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            display: "flex",
            flexDirection: "column",
            paddingBottom: theme.spacing.xl,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <EnglishLoadingWithCompletion
              messages={verificationMessages}
              finalMessageDuration={4000}
              alwaysAnimateFinalStep={true}
              onComplete={() => {
                console.log("✅ PhoneMatch verification loader animation complete");
                setLoaderComplete(true);
              }}
            />
          </Box>
        </Box>
      );
    }
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {!showIntro && !isOtpInput && (
          <Box
            ref={topBarRef}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 5,
              width: "100%",
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              borderBottom: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[3]
              }`,
              padding: "4px 0",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {getPhoneMatchStepTitle()}
            </Text>
          </Box>
        )}

        <Box
          style={{
            marginTop: !showIntro && !isOtpInput ? topBarHeight : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              subtitle={subtitle}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
              skipableText={skipableText}
              onSkip={handleSkip}
            />
          )}

          {!showIntro && !isOtpInput && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Stack spacing="md" style={{ flexGrow: 1, width: "100%" }}>
                {attemptsLeft < reAttempts && !isVerifying && (
                  <Alert
                    mt="xl"
                    mb="md"
                    title="Verification failed"
                    styles={{
                      root: {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[9]
                            : theme.colors.red[0],
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[0]
                            : theme.colors.red[9],
                      },
                      title: {
                        fontSize: "md",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                      message: {
                        fontSize: "sm",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                    }}
                  >
                    Please check that you've entered your correct information and try again.
                  </Alert>
                )}

                {/* Current step fields */}
                <div>
                  {currentStep && (
                    <div
                      key={`${currentStep.key}-${phoneMatchStep}`}
                      style={{
                        ...animatedSectionStyle,
                        width: "100%",
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {currentStep.fields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                            disabled={disabledFields.includes(field.name)}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}
                </div>

                {/* Back button for step 1 */}
                {phoneMatchStep > 0 && (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() =>
                        setPhoneMatchStep((prevStep) => Math.max(prevStep - 1, 0))
                      }
                      c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.gray[6]}
                      sx={{
                        fontSize: "13px",
                        height: "32px",
                        padding: "0 16px",
                        "&:hover": {
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[5]
                              : theme.colors.gray[1],
                        },
                      }}
                    >
                      ← Back
                    </Button>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box
                style={{
                  marginTop: "15px",
                  marginBottom: "40px",
                  width: "100%",
                }}
              >
                <Button
                  fullWidth
                  size="lg"
                  type={getButtonType()}
                  disabled={isVerifying || !isPhoneMatchStepValid()}
                  loading={false}
                  onClick={(e) => {
                    if (phoneMatchStep < lastEditableStepIndex) {
                      e.preventDefault();
                      setPhoneMatchStep((prevStep) =>
                        Math.min(prevStep + 1, lastEditableStepIndex),
                      );
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Sending Data</Text>
                    </MantineGroup>
                  ) : (
                    getButtonText()
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* OTP Input Screen */}
          {!showIntro && isOtpInput && (
            <Box
              style={{
                ...animatedSectionStyle,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box style={{ flexGrow: 1 }}>
                <Center mt={inputSize === "md" ? 25 : 10}>
                  <Stack align="center">
                    <ThemeIcon
                      variant="light"
                      c="teal"
                      size={inputSize === "md" ? 70 : 65}
                      radius={"xl"}
                    >
                      <IconShieldCheck
                        stroke={1}
                        size={inputSize === "md" ? 50 : 45}
                        color="teal"
                      />
                    </ThemeIcon>

                    <Text fz={inputSize === "md" ? 30 : 23} fw={700}>
                      {text?.phonematch?.code?.title || "Enter verification code"}
                    </Text>
                    <Text
                      ta="center"
                      c="dimmed"
                      fz={inputSize === "md" ? 24 : 14}
                    >
                      {text?.phonematch?.code?.subTitle || "We sent a code to your phone"}
                    </Text>
                  </Stack>
                </Center>

                <MantineGroup position="center" mt={40}>
                  <FocusTrap>
                    <PinInput
                      data-autofocus
                      type="number"
                      oneTimeCode
                      size={"xl"}
                      length={6}
                      {...form.getInputProps("pin")}
                      onComplete={() => {
                        submitHandler();
                      }}
                    />
                  </FocusTrap>
                </MantineGroup>

              </Box>

              <Box
                style={{
                  marginTop: "15px",
                  marginBottom: "40px",
                  width: "100%",
                }}
              >
                <Stack align="center" spacing={0}>
                  <Text fz="xs" c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[6]}>
                    {text?.phonematch?.code?.newCodeNote || "Didn't receive a code?"}
                  </Text>
                  <Button
                    onClick={getNewPin}
                    variant="subtle"
                    styles={{
                      root: {
                        color: theme.colorScheme === "dark" ? theme.colors.gray[3] : theme.colors.dark[6],
                      },
                    }}
                  >
                    {text?.phonematch?.code?.newCodeButton || "Get new code"}
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default PhoneMatchForm;
